// Copyright 2022, Imprivata, Inc.  All rights reserved.

import { Button, HexagonSpinner } from '@imprivata-cloud/patient-access-ui-components';
import { Flex, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetGrantStatusQuery } from '../../api/hooks/getGrantStatusQuery';
import { useGetPatientConsentConfigQuery } from '../../api/hooks/getPatientConsentConfigQuery';
import { usePatientConsentMutation } from '../../api/hooks/patientConsentMutation';
import { usePostGrantMutation } from '../../api/hooks/postGrantMutation';
import { ErrorComponent } from '../error/ErrorComponent';
import { PatientConsentDeclineScreen } from './PatientConsentDeclineScreen';
import './PatientConsentStep.less';

interface PatientConsentStepProps {
  onContinue: () => void;
}

export const PatientConsentStep = ({ onContinue }: PatientConsentStepProps) => {
  const { t } = useTranslation();
  const { config, isLoading, isError, setEnabled } = useGetPatientConsentConfigQuery();
  const { mutate, isPending, isError: isAnswerError } = usePatientConsentMutation();
  const { grantStatus, enable: enableGrantStatus } = useGetGrantStatusQuery(false);
  const { postGrant } = usePostGrantMutation();
  const [declineResult, setDeclineResult] = useState<{ redirectUrl: string | null } | null>(null);

  useEffect(() => {
    setEnabled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only an error needs the grant status (for the Return to MyChart action); fetching it earlier
  // would leave a cached copy for the enroll and verify pages, which expect a fresh fetch.
  const hasError = isError || isAnswerError;
  useEffect(() => {
    if (hasError) {
      enableGrantStatus();
    }
  }, [hasError, enableGrantStatus]);

  useEffect(() => {
    if (config && !config.documentUrl) {
      onContinue();
    }
  }, [config, onContinue]);

  if (hasError) {
    return (
      <ErrorComponent
        cause="unknown"
        action={
          grantStatus?.pendingAuthorization
            ? { label: t('patientConsentPage.returnToMyChartButton'), onClick: () => postGrant() }
            : undefined
        }
      />
    );
  }

  if (isLoading || !config) {
    return <HexagonSpinner data-testid="loading-spinner" />;
  }

  if (!config.documentUrl) {
    return null;
  }

  if (declineResult) {
    return (
      <PatientConsentDeclineScreen
        redirectUrl={declineResult.redirectUrl}
        onGoBack={() => setDeclineResult(null)}
      />
    );
  }

  return (
    <Flex vertical align="center" className="w-100" gap={16} data-testid="patient-consent-step">
      <Typography.Title level={3} className="text-center">
        {t('patientConsentPage.title')}
      </Typography.Title>
      <Typography.Text className="text-center">
        {t('patientConsentPage.description')}
      </Typography.Text>
      <div className="patient-consent-step__iframe-container">
        <iframe
          src={config.documentUrl}
          sandbox="allow-popups"
          title={t('patientConsentPage.iframeLabel')}
          data-testid="consent-iframe"
          className="patient-consent-step__iframe"
        />
      </div>
      <Button
        variant="primary"
        onClick={() =>
          mutate(
            { documentId: config.documentId, outcome: 'GRANTED' },
            { onSuccess: onContinue },
          )
        }
        disabled={isPending}
        label={t('patientConsentPage.acceptButton')}
        className="w-100"
        data-testid="accept-button"
      />
      <Button
        variant="secondary"
        onClick={() =>
          mutate(
            { documentId: config.documentId, outcome: 'DECLINED' },
            { onSuccess: (response) => setDeclineResult({ redirectUrl: response.redirectUrl }) },
          )
        }
        disabled={isPending}
        label={t('patientConsentPage.declineButton')}
        className="w-100"
        data-testid="decline-button"
      />
    </Flex>
  );
};
